import {
  Package,
  ShoppingCart,
  ShoppingBag,
  ArrowRightLeft,
  Settings2,
  TrendingUp,
  TrendingDown,
  Layers,
  Calendar,
  Building2,
  Eye,
  X,
  Barcode,
  User,
} from 'lucide-react'
import React, { useState, useCallback, useEffect } from 'react'
import { toast } from 'sonner'

// ── Componentes Globales ──────────────────────────────────────────────────────
import { CatalogSelect } from '../components/ui/CatalogSelect'
import { LocalSelect } from '../components/ui/LocalSelect'
import { SearchFilter } from '../components/ui/SearchFilter'
import { Button } from '../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { TableSkeleton } from '../components/ui/table-skeleton'
// ── Servicio ──────────────────────────────────────────────────────────────────
import {
  inventoryMovementsService,
  InventoryMovement,
  MovementType,
} from '../services/inventory-movements.service'

// ─────────────────────────────────────────────────────────────────────────────
// Configuración de tipos de movimiento
// ─────────────────────────────────────────────────────────────────────────────

interface MovementConfig {
  label: string
  shortLabel: string
  bgColor: string
  textColor: string
  ringColor: string
  Icon: React.ElementType
  direction: 'in' | 'out' | 'neutral'
}

const MOVEMENT_CONFIG: Record<MovementType, MovementConfig> = {
  purchase: {
    label: 'Ingreso por compra',
    shortLabel: 'Compra',
    bgColor: 'bg-emerald-100',
    textColor: 'text-emerald-700',
    ringColor: 'ring-emerald-200',
    Icon: ShoppingBag,
    direction: 'in',
  },
  sale: {
    label: 'Salida por venta',
    shortLabel: 'Venta',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    ringColor: 'ring-blue-200',
    Icon: ShoppingCart,
    direction: 'out',
  },
  manual_stock_adjustment_in: {
    label: 'Ajuste manual (entrada)',
    shortLabel: 'Ajuste +',
    bgColor: 'bg-violet-100',
    textColor: 'text-violet-700',
    ringColor: 'ring-violet-200',
    Icon: Settings2,
    direction: 'in',
  },
  manual_stock_adjustment_out: {
    label: 'Ajuste manual (salida)',
    shortLabel: 'Ajuste −',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-700',
    ringColor: 'ring-amber-200',
    Icon: Settings2,
    direction: 'out',
  },
  transfer_in: {
    label: 'Ingreso por transferencia',
    shortLabel: 'Transferencia +',
    bgColor: 'bg-cyan-100',
    textColor: 'text-cyan-700',
    ringColor: 'ring-cyan-200',
    Icon: ArrowRightLeft,
    direction: 'in',
  },
  transfer_out: {
    label: 'Salida por transferencia',
    shortLabel: 'Transferencia −',
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-700',
    ringColor: 'ring-orange-200',
    Icon: ArrowRightLeft,
    direction: 'out',
  },
  transfer_not_received: {
    label: 'Transferencia no recibida',
    shortLabel: 'Trans. Fallida',
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
    ringColor: 'ring-red-200',
    Icon: ArrowRightLeft,
    direction: 'neutral',
  },
  import_initial_stock: {
    label: 'Stock inicial por importación',
    shortLabel: 'Inicial (Imp)',
    bgColor: 'bg-slate-100',
    textColor: 'text-slate-600',
    ringColor: 'ring-slate-200',
    Icon: Layers,
    direction: 'in',
  },
  import_stock_adjustment: {
    label: 'Ajuste por importación',
    shortLabel: 'Ajuste (Imp)',
    bgColor: 'bg-indigo-100',
    textColor: 'text-indigo-700',
    ringColor: 'ring-indigo-200',
    Icon: Layers,
    direction: 'neutral',
  },
}

const DEFAULT_MOVEMENT_CONFIG: MovementConfig = {
  label: 'Movimiento',
  shortLabel: 'Otro',
  bgColor: 'bg-slate-100',
  textColor: 'text-slate-600',
  ringColor: 'ring-slate-200',
  Icon: Package,
  direction: 'neutral',
}

function getMovementConfig(type: string): MovementConfig {
  return MOVEMENT_CONFIG[type as MovementType] ?? DEFAULT_MOVEMENT_CONFIG
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers de fecha
// ─────────────────────────────────────────────────────────────────────────────

function todayISO() {
  return new Date().toLocaleDateString('sv-SE')
}

function yesterdayISO() {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return d.toLocaleDateString('sv-SE')
}

function formatDate(raw: string): string {
  try {
    const d = new Date(raw)
    const now = new Date()
    const isToday = d.toDateString() === now.toDateString()
    const yesterday = new Date(now)
    yesterday.setDate(now.getDate() - 1)
    const isYesterday = d.toDateString() === yesterday.toDateString()
    const time = d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
    if (isToday) return `Hoy · ${time}`
    if (isYesterday) return `Ayer · ${time}`
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) + ` · ${time}`
  } catch {
    return raw
  }
}

function formatQuantity(value: number | string | null): string {
  if (value === null) return 'Sin capturar'

  const numericValue = Number(value)

  return Number.isFinite(numericValue)
    ? new Intl.NumberFormat('es-MX', { maximumFractionDigits: 3 }).format(numericValue)
    : String(value)
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal de Detalle
// ─────────────────────────────────────────────────────────────────────────────

function MovementDetailModal({
  movement,
  onClose,
}: {
  movement: InventoryMovement | null
  onClose: () => void
}) {
  if (!movement) return null
  const cfg = getMovementConfig(movement.type)
  const affectsStock = movement.affects_stock !== false

  return (
    <Dialog open={!!movement} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <cfg.Icon className="h-5 w-5" />
            Detalle del movimiento
          </DialogTitle>
          <DialogDescription className="sr-only">
            Información de trazabilidad, producto y existencias del movimiento seleccionado.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Tipo + dirección */}
          <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${cfg.bgColor}`}>
              <cfg.Icon className={`h-5 w-5 ${cfg.textColor}`} />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-slate-900">{cfg.label}</p>
              <p className="text-xs text-slate-500">{formatDate(movement.created_at)}</p>
            </div>
            <div className="ml-auto shrink-0">
              {!affectsStock ? (
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                  Sin movimiento
                </span>
              ) : cfg.direction === 'in' ? (
                <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                  <TrendingUp className="h-3 w-3" /> Entrada
                </span>
              ) : cfg.direction === 'out' ? (
                <span className="flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700">
                  <TrendingDown className="h-3 w-3" /> Salida
                </span>
              ) : (
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                  Neutro
                </span>
              )}
            </div>
          </div>

          {/* Producto */}
          <section className="rounded-lg border border-slate-200 bg-white p-4">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Producto</h3>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div className="col-span-2">
                <dt className="text-slate-500">Nombre</dt>
                <dd className="font-medium text-slate-900">{movement.product_name}</dd>
              </div>
              {movement.product_barcode && (
                <div>
                  <dt className="text-slate-500">Código de barras</dt>
                  <dd className="font-mono text-slate-700 flex items-center gap-2"><Barcode className='h-3 w-3' /> {movement.product_barcode}</dd>
                </div>
              )}
              {movement.batch_number && (
                <div>
                  <dt className="text-slate-500">Lote</dt>
                  <dd className="font-mono text-slate-700">{movement.batch_number}</dd>
                </div>
              )}
            </dl>
          </section>

          {/* Cantidad y stock */}
          <section className="rounded-lg border border-slate-200 bg-white p-4">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Cantidad</h3>
            <dl className="grid grid-cols-3 gap-x-4 gap-y-2 text-sm">
              <div>
                <dt className="text-slate-500">Movimiento</dt>
                <dd className={`text-lg font-bold ${affectsStock && cfg.direction === 'in' ? 'text-emerald-600' : affectsStock && cfg.direction === 'out' ? 'text-red-600' : 'text-slate-700'}`}>
                  {formatQuantity(movement.quantity)}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">Stock resultante</dt>
                <dd className="text-lg font-bold text-slate-900">
                  {movement.stock_after === null ? 'Sin capturar' : `${movement.stock_after} u.`}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">Sucursal</dt>
                <dd className="text-sm font-medium text-slate-700">{movement.sucursal_name}</dd>
              </div>
            </dl>
          </section>

          {/* Proveedor / motivo */}
          {(movement.supplier_name || movement.purchase_reference_number || movement.reason) && (
            <section className="rounded-lg border border-slate-200 bg-white p-4">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Contexto</h3>
              <dl className="space-y-2 text-sm">
                {movement.supplier_name && (
                  <div>
                    <dt className="text-slate-500">Proveedor</dt>
                    <dd className="font-medium text-slate-900">{movement.supplier_name}</dd>
                  </div>
                )}
                {movement.purchase_reference_number && (
                  <div>
                    <dt className="text-slate-500">Número de nota</dt>
                    <dd className="font-mono font-medium text-slate-900">{movement.purchase_reference_number}</dd>
                  </div>
                )}
                {movement.reason && (
                  <div>
                    <dt className="text-slate-500">Motivo</dt>
                    <dd className="text-slate-700">{movement.reason}</dd>
                  </div>
                )}
              </dl>
            </section>
          )}

          {/* Usuario y referencia */}
          <section className="rounded-lg border border-slate-100 bg-slate-50 p-4">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">Trazabilidad</h3>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs text-slate-500">
              {movement.user_name && (
                <div className="col-span-2">
                  <dt className="text-slate-400">Usuario</dt>
                  <dd className="flex items-center gap-1.5 font-medium text-slate-700">
                    <User className="h-3 w-3 text-slate-400" />
                    {movement.user_name}
                    {movement.user_email && (
                      <span className="text-slate-400 font-normal">· {movement.user_email}</span>
                    )}
                  </dd>
                </div>
              )}
              {movement.reference_id && (
                <div>
                  <dt className="text-slate-400">Referencia</dt>
                  <dd className="font-mono">#{movement.reference_id}</dd>
                  <dd className="font-mono">({movement.reference_type})</dd>
                </div>
              )}
              <div>
                <dt className="text-slate-400">ID del registro</dt>
                <dd className="font-mono">#{movement.id}</dd>
              </div>
            </dl>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Paginación local
// ─────────────────────────────────────────────────────────────────────────────

interface PaginationState {
  currentPage: number
  total: number
  perPage: number
  lastPage: number
  from: number
  to: number
}

const INITIAL_PAGINATION: PaginationState = {
  currentPage: 1, total: 0, perPage: 10, lastPage: 1, from: 0, to: 0,
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente Principal
// ─────────────────────────────────────────────────────────────────────────────

export function InventoryMovements() {
  // ── Estado de datos ────────────────────────────────────────────────────────
  const [movements, setMovements] = useState<InventoryMovement[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [pagination, setPagination] = useState<PaginationState>(INITIAL_PAGINATION)
  const [selectedMovement, setSelectedMovement] = useState<InventoryMovement | null>(null)

  // ── Filtros ────────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<MovementType | 'all'>('all')
  const [filterFrom, setFilterFrom] = useState('')
  const [filterTo, setFilterTo] = useState('')
  /** ID de sucursal seleccionada — string vacío = todas */
  const [filterSucursalId, setFilterSucursalId] = useState('')
  /** ID de producto seleccionado — string vacío = todos */
  const [filterProductId, setFilterProductId] = useState('')

  // ── Acceso rápido de fecha ────────────────────────────────────────────────
  const setQuickDate = (range: 'today' | 'yesterday') => {
    if (range === 'today') {
      const t = todayISO()
      setFilterFrom(t)
      setFilterTo(t)
    } else {
      const y = yesterdayISO()
      setFilterFrom(y)
      setFilterTo(y)
    }
  }

  const clearDates = () => {
    setFilterFrom('')
    setFilterTo('')
  }

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchMovements = useCallback(async (page = 1) => {
    setIsLoading(true)
    try {
      const params = {
        page,
        per_page: 10,
        search: searchQuery || undefined,
        type: filterType !== 'all' ? filterType : undefined,
        from: filterFrom || undefined,
        to: filterTo || undefined,
        sucursal_id: filterSucursalId ? Number(filterSucursalId) : undefined,
        product_id: filterProductId ? Number(filterProductId) : undefined,
      }

      const res = await inventoryMovementsService.getMovements(params)

      if (res?.data && Array.isArray(res.data)) {
        setMovements(res.data)
        setPagination({
          currentPage: res.current_page,
          total: res.total,
          perPage: res.per_page,
          lastPage: res.last_page,
          from: res.from ?? 0,
          to: res.to ?? 0,
        })
      } else {
        setMovements([])
        setPagination(INITIAL_PAGINATION)
      }
    } catch {
      toast.error('Error al cargar los movimientos de inventario')
    } finally {
      setIsLoading(false)
    }
  }, [searchQuery, filterType, filterFrom, filterTo, filterSucursalId, filterProductId])

  useEffect(() => {
    fetchMovements(1)
  }, [fetchMovements])

  const handlePageChange = (page: number) => fetchMovements(page)

  const hasActiveFilters =
    searchQuery || filterType !== 'all' || filterFrom || filterTo || filterSucursalId || filterProductId

  const resetFilters = () => {
    setSearchQuery('')
    setFilterType('all')
    setFilterFrom('')
    setFilterTo('')
    setFilterSucursalId('')
    setFilterProductId('')
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 p-6 w-full max-w-full min-w-0">
      {/* ── Header ── */}
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold text-slate-900">Trazabilidad de Movimientos</h1>
        <p className="text-sm text-slate-500">
          Historial completo de entradas, salidas y ajustes de inventario por producto.
        </p>
      </div>

      {/* ── Panel de Filtros ── */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">

          <SearchFilter
            initialValue={searchQuery}
            placeholder="Producto, código, proveedor o nota..."
            onSearch={setSearchQuery}
          />

          {/* Producto */}
          <CatalogSelect
            catalog="products"
            placeholder="Todos los productos"
            value={filterProductId}
            onChange={(val: string) =>
              setFilterProductId(val === filterProductId ? '' : val)
            }
          />

          {/* Tipo de movimiento */}
          <LocalSelect
            options={(Object.entries(MOVEMENT_CONFIG) as [MovementType, MovementConfig][]).map(
              ([key, cfg]) => ({ value: key, label: cfg.label })
            )}
            value={filterType === 'all' ? '' : filterType}
            onChange={(val) => setFilterType(val ? (val as MovementType) : 'all')}
            placeholder="Todos los tipos"
          />

          {/* Sucursal */}
          <CatalogSelect
            catalog="sucursales"
            placeholder="Todas las sucursales"
            value={filterSucursalId}
            onChange={(val: string) =>
              setFilterSucursalId(val === filterSucursalId ? '' : val)
            }
          />

          {/* Limpiar filtros */}
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="flex h-9 items-center gap-1.5 rounded-md border border-slate-200 px-3 text-sm text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
              >
                <X className="h-3.5 w-3.5" /> Limpiar
              </button>
            )}
          </div>
        </div>

        {/* Segunda fila: rango de fechas + accesos rápidos */}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            <Calendar className="h-4 w-4 text-slate-400 shrink-0" />
            <span className="text-sm text-slate-500">Desde:</span>
            <input
              type="date"
              value={filterFrom}
              max={filterTo || todayISO()}
              onChange={(e) => setFilterFrom(e.target.value)}
              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-slate-500">Hasta:</span>
            <input
              type="date"
              value={filterTo}
              min={filterFrom}
              max={todayISO()}
              onChange={(e) => setFilterTo(e.target.value)}
              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          {/* Accesos rápidos de fecha */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setQuickDate('today')}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${filterFrom === todayISO() && filterTo === todayISO()
                ? 'bg-primary text-white'
                : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
            >
              Hoy
            </button>
            <button
              onClick={() => setQuickDate('yesterday')}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${filterFrom === yesterdayISO() && filterTo === yesterdayISO()
                ? 'bg-primary text-white'
                : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
            >
              Ayer
            </button>
            {(filterFrom || filterTo) && (
              <button
                onClick={clearDates}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors"
              >
                × Limpiar fechas
              </button>
            )}
          </div>
        </div>
      </div>


      {/* ── Tabla ── */}
      {isLoading ? (
        <TableSkeleton
          headers={['Fecha', 'Producto', 'Tipo', 'Cantidad', 'Stock tras mov.', 'Sucursal', 'Usuario', 'Motivo', 'Acciones']}
          columns={['text', 'text-two-lines', 'badge', 'text', 'text', 'text', 'text', 'text', 'actions']}
          rows={8}
        />
      ) : (
        <div className="w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Producto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Tipo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Cantidad</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Stock resultante</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Sucursal</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Usuario</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Motivo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {movements.length > 0 ? (
                  movements.map((mov) => {
                    const cfg = getMovementConfig(mov.type)
                    const isIn = cfg.direction === 'in' && mov.affects_stock
                    const isOut = cfg.direction === 'out'

                    return (
                      <tr
                        key={mov.row_key}
                        className="group transition-colors hover:bg-slate-50/60"
                      >
                        {/* Fecha */}
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {formatDate(mov.created_at)}
                        </td>

                        {/* Producto */}
                        <td className="px-4 py-3 flex flex-col">
                          <p className="font-medium text-slate-900 table-cell-truncate" title={mov.product_name}>
                            {mov.product_name}
                          </p>
                          {mov.product_barcode && (
                            <p className="mt-0.5 font-mono text-[11px] text-slate-400 table-cell-truncate flex items-center gap-2" title={mov.product_barcode}>
                              <Barcode className="h-3 w-3" /> {mov.product_barcode}
                            </p>
                          )}
                          {mov.batch_number && (
                            <p className="mt-0.5 text-[11px] text-slate-400 table-cell-truncate" title={`Lote: ${mov.batch_number}`}>
                              Lote: {mov.batch_number}
                            </p>
                          )}
                        </td>

                        {/* Tipo de movimiento */}
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${cfg.bgColor} ${cfg.textColor}`}
                          >
                            <cfg.Icon className="h-3 w-3" />
                            {cfg.shortLabel}
                          </span>
                          {/* Indicador de proveedor visual */}
                          {mov.type === 'purchase' && mov.supplier_name && (
                            <p className="mt-1 text-[11px] text-slate-400">
                              {mov.supplier_name}
                            </p>
                          )}
                        </td>

                        {/* Cantidad */}
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-sm font-bold ${isIn
                              ? 'bg-emerald-50 text-emerald-700'
                              : isOut
                                ? 'bg-red-50 text-red-700'
                                : 'bg-slate-100 text-slate-700'
                              }`}
                          >
                            {isIn ? (
                              <TrendingUp className="h-3.5 w-3.5" />
                            ) : isOut ? (
                              <TrendingDown className="h-3.5 w-3.5" />
                            ) : null}
                            {formatQuantity(mov.quantity)}
                          </span>
                        </td>

                        {/* Stock resultante */}
                        <td className="px-4 py-3">
                          <span className="text-sm font-semibold text-slate-900">
                            {mov.stock_after === null ? 'Sin capturar' : `${mov.stock_after} u.`}
                          </span>
                        </td>

                        {/* Sucursal */}
                        <td className="px-4 py-3">
                          <span className="flex items-center gap-1.5 text-sm text-slate-600">
                            <Building2 className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                            {mov.sucursal_name}
                          </span>
                        </td>

                        {/* Usuario */}
                        <td className="px-4 py-3">
                          {mov.user_name ? (
                            <span className="flex items-center gap-1.5 text-sm text-slate-700">
                              <User className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                              <span className="table-cell-truncate" title={mov.user_name}>{mov.user_name}</span>
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400 italic">Sistema</span>
                          )}
                        </td>

                        {/* Motivo / razón */}
                        <td className="px-4 py-3">
                          {/* Motivo para ajustes */}
                          {mov.reason && (
                            <p className="mt-0.5 table-cell-truncate text-[11px] italic text-slate-400" title={mov.reason}>
                              {mov.reason}
                            </p>
                          )}
                        </td>

                        {/* Acción */}
                        <td className="px-4 py-3">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedMovement(mov)}
                            className="rounded p-1.5 text-slate-600 transition-colors hover:bg-slate-200"
                            title="Ver detalle"
                          >
                            <Eye className="h-4 w-4 text-slate-400" />
                          </Button>
                        </td>
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan={9} className="px-4 py-16 text-center">
                      <Package className="mx-auto mb-3 h-10 w-10 text-slate-200" />
                      <p className="text-sm font-medium text-slate-500">
                        No se encontraron movimientos
                      </p>
                      <p className="mt-1 text-xs text-slate-400">
                        Ajusta los filtros o el rango de fechas
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {pagination.total > 0 && (
            <GlobalPaginator
              totalItems={pagination.total}
              itemsPerPage={pagination.perPage}
              currentPage={pagination.currentPage}
              from={pagination.from}
              to={pagination.to}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      )}

      {/* Modal de detalle */}
      <MovementDetailModal
        movement={selectedMovement}
        onClose={() => setSelectedMovement(null)}
      />
    </div>
  )
}
